import { useState } from "react";
import {
  Box, Typography, Paper, TextField, InputAdornment, Alert, Stack, Button, Chip,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi, type UserListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { UserForm } from "./UserForm";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resetPwdId, setResetPwdId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<UserListDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("admin.user.create"));
  const canUpdate = useAuth(s => s.hasPermission("admin.user.edit"));
  const canReset = useAuth(s => s.hasPermission("admin.user.reset_password"));
  const canUnlock = useAuth(s => s.hasPermission("admin.user.unlock"));
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["users", search, pagination.page, pagination.pageSize],
    queryFn: () => usersApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const unlockMutation = useMutation({
    mutationFn: (id: string) => usersApi.unlock(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const openCreate = () => { setEditingId(null); setFormOpen(true); };
  const openEdit = (id: string) => { setEditingId(id); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };

  const columns: GridColDef<UserListDto>[] = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "fullName", headerName: "Full name", flex: 1, minWidth: 180 },
    {
      field: "isActive", headerName: "Status", width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} />
      ),
    },
    {
      field: "isLockedOut", headerName: "Locked", width: 100,
      renderCell: (p) => p.value
        ? <Chip size="small" label="Locked" color="error" />
        : <Chip size="small" label="OK" variant="outlined" />,
    },
    {
      field: "mfaEnabled", headerName: "MFA", width: 80,
      renderCell: (p) => p.value ? <Chip size="small" label="On" color="info" /> : null,
    },
    { field: "branchAccessCount", headerName: "Branches", width: 100, type: "number" },
    {
      field: "createdAt", headerName: "Created", width: 130,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "",
    },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Users</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New User
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by email or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {unlockMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(unlockMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canUpdate && openEdit(p.row.id)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canUpdate && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow.id)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canReset && (
          <MenuItem onClick={() => { if (menuRow) setResetPwdId(menuRow.id); closeMenu(); }}>
            <ListItemIcon><VpnKeyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Reset password</ListItemText>
          </MenuItem>
        )}
        {canUnlock && menuRow?.isLockedOut && (
          <MenuItem onClick={() => {
            if (menuRow) unlockMutation.mutate(menuRow.id);
            closeMenu();
          }}>
            <ListItemIcon><LockOpenIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Unlock</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <UserForm open={formOpen} onClose={() => setFormOpen(false)} userId={editingId} />
      <ResetPasswordDialog userId={resetPwdId} open={!!resetPwdId} onClose={() => setResetPwdId(null)} />
    </Box>
  );
}
